"use client";

import React, { useState } from "react";
import { useCart } from "@/packages/context/CartContext";
import { Trash2, Heart, ArrowLeft, ShoppingBag } from "lucide-react";
import Price from "@/components/common/Price";
import CartSummary from "@/components/cart/CartSummary";
// import CartRecommendations from "@/components/cart/CartRecommendations";
import CartTrustBadges from "@/components/cart/CartTrustBadges";
import { Variant, VariantAttribute } from "@/packages/types/products";
import QuantitySelector from "@/components/common/QuantitySelector";
import Badge from "@/components/common/Badge";
// import Link from "next/link";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import NextImage from "@/components/common/NextImage";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import RatingStars from "@/components/common/RatingStar";
import { useFreeShippingThreshold } from "@/packages/hooks/useFreeShippingThreshold";
import { useCurrency } from "@/packages/context/CurrencyContext";
import { useWishlist } from "@/packages/context/wishlistContext";
import { useAuth } from "@/packages/context/AuthContext";
import { toast } from "sonner";
import LABELS from "@/components/global-constant/src/general";

export default function CartPage() {
  const { symbol, code, exchangeRate } = useCurrency();
  const freeShippingThreshold = useFreeShippingThreshold();
  const { cartItems, removeCart, updateCart, previousOrderId, setPreviousOrderId } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { isLoggedIn } = useAuth();
  const { fastPush } = useFastRouter();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number | string; variant?: Variant } | null>(null);

  const handleQuantityChange = (
    id: number | string,
    newQuantity: number,
    variant?: Variant
  ) => {
    updateCart(id, newQuantity, variant);
  };

  const handleRemoveItem = (id: number | string, variant?: Variant) => {
    setItemToDelete({ id, variant });
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      removeCart(itemToDelete.id, itemToDelete.variant);
    }
    setIsConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsConfirmOpen(false);
    setItemToDelete(null);
  };

  const goToProduct = (slug: string) => {
    fastPush(`/products/${slug}`);
  };

  // Subtotal & free shipping calculations
  const subtotalRaw = cartItems.reduce(
    (sum, item) => sum + item.quantity * (item.finalPrice ?? item.price),
    0
  );
  const subtotalConverted = subtotalRaw * exchangeRate;
  const thresholdAmount = freeShippingThreshold ?? 0;
  const neededForFreeShipping = Math.max(0, thresholdAmount - subtotalConverted);
  const shippingProgressPct = thresholdAmount > 0 
    ? Math.min(100, Math.round((subtotalConverted / thresholdAmount) * 100)) 
    : 100;

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 pb-28 md:pb-8 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200/80">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Shopping Cart
            </h1>
            <p className="text-xs text-slate-500 font-medium mt-1">
              {cartItems.length} {cartItems.length === 1 ? "item" : "items"} in your bag
            </p>
          </div>

          <button
            type="button"
            onClick={() => fastPush("/")}
            className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-[var(--brand-accent)] transition-colors cursor-pointer w-fit"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Continue Shopping</span>
          </button>
        </div>

        {/* Retry Payment Order Banner */}
        {previousOrderId && (
          <div className="p-4 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl flex items-center justify-between text-xs font-medium shadow-xs">
            <div className="flex items-center gap-2">
              <span className="text-base animate-pulse">⚡</span>
              <span>
                Retrying payment for Order <strong className="font-bold">#{previousOrderId}</strong>. Checking out will replace your unpaid order.
              </span>
            </div>
            <button
              onClick={() => setPreviousOrderId(null)}
              className="text-xs text-amber-700 hover:text-amber-900 underline font-semibold transition cursor-pointer shrink-0 ml-4"
            >
              Cancel Retry
            </button>
          </div>
        )}

        {/* Two-Column Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Main Section (70% on desktop) */}
          <div className="lg:col-span-8 space-y-6">

            {/* Free Shipping Progress Card */}
            {freeShippingThreshold !== null && cartItems.length > 0 && (
              <div className="p-4 sm:p-5 bg-[#F4FBF6] border border-[#E2F4E8] rounded-[12px] space-y-3 shadow-xs">
                <p className="text-[15px] font-semibold text-slate-800 leading-snug">
                  {neededForFreeShipping > 0 ? (
                    <>
                      You&apos;re only <span className="font-bold text-slate-900">{symbol}{neededForFreeShipping.toFixed(2)} {code.toUpperCase()}</span> away from <span className="text-[#16A34A] font-bold">FREE Shipping</span>
                    </>
                  ) : (
                    <>
                      You qualify for <span className="text-[#16A34A] font-bold">FREE Shipping</span>!
                    </>
                  )}
                </p>

                <div className="w-full h-[6px] bg-[#E5E7EB] rounded-[3px] overflow-hidden">
                  <div
                    className="h-full bg-[#22C55E] transition-all duration-300 rounded-[3px]"
                    style={{ width: `${shippingProgressPct}%` }}
                  />
                </div>

                <p className="text-[12px] text-slate-500 font-medium">
                  Free shipping on orders over <span className="font-semibold text-slate-700">{symbol}{thresholdAmount.toFixed(2)} {code.toUpperCase()}</span>
                </p>
              </div>
            )}

            {/* Cart Items List */}
            {cartItems.length === 0 ? (
              <div className="bg-white rounded-[12px] border border-slate-200/80 p-12 text-center space-y-4 shadow-xs">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mx-auto">
                  <ShoppingBag className="w-8 h-8 opacity-50" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{LABELS.CART.EMPTY_MESSAGE}</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Your shopping cart is currently empty. Explore our bestsellers and discover pure handcrafted products.
                </p>
                <div className="pt-2">
                  <button
                    onClick={() => fastPush("/")}
                    className="inline-flex items-center gap-2 bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white text-xs font-bold px-6 py-3 rounded-xl shadow-md transition-colors cursor-pointer"
                  >
                    {LABELS.CART.CONTINUE_SHOPPING}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item) => {
                  const isFree = String(item.id).includes("-free");

                  const variantImage =
                    item.variant && item.images
                      ? item.images.find(
                          (img) => img.id === item.variant?.imageId
                        )
                      : null;

                  const imageUrl =
                    variantImage?.imageUrl ||
                    item?.images?.[0]?.imageUrl ||
                    "/assets/site/images/logo.png";

                  const variantAttributes =
                    item.variant?.variantAttributes
                      ?.map((attr: VariantAttribute) => attr.attribute_value?.name || "")
                      .filter(Boolean)
                      .join(" / ") || null;

                  const stock = item.variant?.stock ?? item.stock ?? Infinity;
                  const isStockAvailable = stock > 0;
                  const itemSubtotal = Number(item.finalPrice ?? item.price) * item.quantity;

                  return (
                    <div
                      key={`${item.id}-${item.variant?.id ?? "no-variant"}`}
                      className="bg-white rounded-[12px] border border-slate-200/80 p-4 sm:p-5 shadow-xs transition-all hover:border-slate-300 flex flex-col sm:flex-row gap-4 items-start"
                    >
                      {/* Product Image */}
                      <div
                        onClick={() => !isFree && goToProduct(item.slug)}
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg border border-slate-100 bg-slate-50 overflow-hidden shrink-0 cursor-pointer p-1"
                      >
                        <NextImage
                          src={imageUrl?.trim() || "/assets/site/images/logo.png"}
                          alt={item.title}
                          width={96}
                          height={96}
                          className="w-full h-full object-contain"
                        />
                      </div>

                      {/* Product Info & Actions */}
                      <div className="flex-1 w-full space-y-2">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                          <div>
                            <h3
                              onClick={() => !isFree && goToProduct(item.slug)}
                              className="text-sm sm:text-base font-bold text-slate-900 line-clamp-2 hover:text-[var(--brand-accent)] cursor-pointer transition-colors"
                            >
                              {item.title}
                            </h3>

                            {/* Variant & SKU */}
                            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500 font-medium">
                              {variantAttributes && (
                                <span>Variant: <strong className="text-slate-700 font-semibold">{variantAttributes}</strong></span>
                              )}
                              {item.variant?.sku && (
                                <span>SKU: <strong className="text-slate-700 font-semibold">{item.variant.sku}</strong></span>
                              )}
                            </div>

                            {/* Rating Stars */}
                            {item.averageRating !== undefined && (
                              <div className="mt-1">
                                <RatingStars rating={item.averageRating} reviewCount={item.totalRating || 0} />
                              </div>
                            )}

                            {/* Stock Badge */}
                            {!isStockAvailable && (
                              <div className="mt-1.5">
                                <Badge text={LABELS.CART.NO_STOCK} className="bg-rose-500 text-white text-[10px]" />
                              </div>
                            )}
                          </div>

                          {/* Item Subtotal Price */}
                          <div className="text-left sm:text-right shrink-0">
                            <span className="text-[10px] text-slate-400 font-medium block">Subtotal</span>
                            <Price amount={itemSubtotal} className="text-base font-bold text-slate-900" />
                          </div>
                        </div>

                        {/* Quantity & Unit Price Row */}
                        <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t border-slate-100">
                          {isFree ? (
                            <div className="text-xs text-emerald-600 font-bold">
                              🎁 Free Promo Product (Qty: {item.quantity})
                            </div>
                          ) : (
                            <div className="flex items-center gap-4">
                              {/* Quantity Selector (Height 44px) */}
                              {isStockAvailable ? (
                                <div onClick={(e) => e.stopPropagation()}>
                                  <QuantitySelector
                                    value={item.quantity}
                                    min={1}
                                    max={stock}
                                    onChange={(newQty) =>
                                      handleQuantityChange(
                                        item.id,
                                        newQty,
                                        item.variant
                                      )
                                    }
                                    className="h-11 border border-slate-200 rounded-xl px-2 flex items-center justify-between min-w-[110px] bg-slate-50/50"
                                  />
                                </div>
                              ) : (
                                <span className="text-xs text-rose-500 font-semibold">
                                  {LABELS.CART.OUT_OF_STOCK}
                                </span>
                              )}

                              {/* Unit Price */}
                              <div className="text-xs text-slate-500 font-medium">
                                <span>Unit: </span>
                                <Price amount={Number(item.finalPrice ?? item.price)} className="font-semibold text-slate-800" />
                              </div>
                            </div>
                          )}

                          {/* Action Buttons: Remove & Save for Later */}
                          {!isFree && (
                            <div className="flex items-center gap-4 text-xs font-semibold">
                              {/* Save for Later */}
                              <button
                                type="button"
                                onClick={() => {
                                  if (!isLoggedIn) {
                                    toast.info("Please sign in to save items to wishlist");
                                    return;
                                  }
                                  toggleWishlist(item);
                                  toast.success("Saved to your Wishlist!");
                                }}
                                className="inline-flex items-center gap-1 text-slate-500 hover:text-rose-500 transition-colors cursor-pointer"
                              >
                                <Heart className={`w-3.5 h-3.5 ${isInWishlist(item.id) ? "text-rose-500 fill-current" : ""}`} />
                                <span>Save for Later</span>
                              </button>

                              {/* Remove */}
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(item.id, item.variant)}
                                className="inline-flex items-center gap-1 text-slate-400 hover:text-rose-600 transition-colors cursor-pointer"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                <span>Remove</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Recommendations & Trust Section Below Items */}
            {/* <CartRecommendations /> */}
            <CartTrustBadges />
          </div>

          {/* Right Section (30% Sticky Cart Sidebar on Desktop - sticks below header) */}
          <div className="lg:col-span-4 lg:sticky lg:top-[175px] lg:self-start">
            <CartSummary />
          </div>
        </div>

      </div>

      {/* Item Delete Modal */}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title={LABELS.CART.CONFIRM_DELETE_TITLE}
        message={LABELS.CART.CONFIRM_DELETE_MSG}
        confirmLabel={LABELS.CART.CONFIRM_LABEL}
        cancelLabel={LABELS.CART.CANCEL_LABEL}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
